import React, { useEffect, useState } from 'react';
import { Mail, Plus, Send, Trash2, Users } from 'lucide-react';
import api from '../lib/api';

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
}

export default function FinalistCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [sel, setSel] = useState(null); // selected campaign object (editable copy)
  const [excludeBooked, setExcludeBooked] = useState(true);
  const [excludeUploaded, setExcludeUploaded] = useState(false);
  const [resendAlreadySent, setResendAlreadySent] = useState(false);
  const [recips, setRecips] = useState(null);
  const [showRecips, setShowRecips] = useState(false);
  const [spotTo, setSpotTo] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  async function load(keepSel) {
    try {
      const items = (await api.get('/admin/awards/campaigns')).data.items || [];
      setCampaigns(items);
      if (!keepSel) setSel(items[0] ? { ...items[0] } : null);
      else if (sel) {
        const cur = items.find((c) => c.id === sel.id);
        if (cur) setSel((s) => ({ ...s, sent_count: cur.sent_count }));
      }
    } catch { setMsg({ ok: false, text: 'Could not load emails' }); }
  }
  useEffect(() => { load(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sel?.id) { setRecips(null); return undefined; }
    const t = setTimeout(() => { reloadRecips(); }, 300);
    return () => clearTimeout(t);
  }, [sel?.id, excludeBooked, excludeUploaded, resendAlreadySent, sel?.sent_count]); // eslint-disable-line react-hooks/exhaustive-deps

  async function reloadRecips() {
    if (!sel?.id) return;
    try {
      setRecips((await api.get(`/admin/awards/campaigns/${sel.id}/recipients`, { params: { exclude_booked: excludeBooked ? 1 : 0, exclude_uploaded: excludeUploaded ? 1 : 0, resend_already_sent: resendAlreadySent ? 1 : 0 } })).data);
    } catch { setRecips(null); }
  }

  async function setOverride(email, action) {
    try {
      await api.post(`/admin/awards/campaigns/${sel.id}/override`, { email, action });
      reloadRecips();
    } catch { setMsg({ ok: false, text: 'Could not update the list' }); }
  }

  async function saveCopy() {
    if (!sel) return;
    setBusy('save'); setMsg(null);
    try {
      await api.patch(`/admin/awards/campaigns/${sel.id}`, { name: sel.name, subject: sel.subject, body: sel.body, bcc: sel.bcc || '' });
      setMsg({ ok: true, text: 'Saved.' });
      load(true);
    } catch { setMsg({ ok: false, text: 'Save failed' }); }
    finally { setBusy(''); }
  }

  async function newCampaign() {
    setBusy('new');
    try {
      const r = await api.post('/admin/awards/campaigns', {
        name: 'New email', subject: sel?.subject || '', body: sel?.body || '', bcc: sel?.bcc || '' });
      await load(false);
      setSel({ ...r.data.campaign });
    } catch { setMsg({ ok: false, text: 'Could not create the email' }); }
    finally { setBusy(''); }
  }

  async function removeCampaign() {
    if (!sel) return;
    if (!window.confirm(`Delete "${sel.name}"? Its send history goes with it.`)) return;
    await api.delete(`/admin/awards/campaigns/${sel.id}`);
    load(false);
  }

  async function spotCheck() {
    if (!sel || !spotTo.trim()) return;
    setBusy('spot'); setMsg(null);
    try {
      await api.patch(`/admin/awards/campaigns/${sel.id}`, { subject: sel.subject, body: sel.body, bcc: sel.bcc || '' });
      const r = await api.post(`/admin/awards/campaigns/${sel.id}/spot-check`, { email: spotTo.trim() });
      setMsg(r.data.ok ? { ok: true, text: `Test email sent to ${spotTo.trim()} — nothing sent to finalists.` } : { ok: false, text: 'Test send failed — check the Resend quota.' });
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Test send failed' }); }
    finally { setBusy(''); }
  }

  async function sendAll() {
    if (!sel || !recips) return;
    const n = recips.counts.will_send;
    if (!window.confirm(`Send "${sel.name}" to ${n} finalist business${n === 1 ? '' : 'es'} now? ${(recips.counts.excluded_booked || 0) + (recips.counts.excluded_uploaded || 0)} are excluded by your filters. ${resendAlreadySent ? 'FOLLOW-UP MODE: businesses that already had this email WILL receive it again (unless excluded by the filters above).' : 'Anyone already sent this email is skipped automatically.'}`)) return;
    setBusy('send'); setMsg(null);
    try {
      await api.patch(`/admin/awards/campaigns/${sel.id}`, { subject: sel.subject, body: sel.body, bcc: sel.bcc || '' });
      const r = await api.post(`/admin/awards/campaigns/${sel.id}/send`, { exclude_booked: excludeBooked, exclude_uploaded: excludeUploaded, resend_already_sent: resendAlreadySent });
      const failed = r.data.failed || [];
      setMsg({ ok: failed.length === 0,
        text: `Sent ${r.data.sent}. ${r.data.excluded_booked ? `${r.data.excluded_booked} excluded by filters. ` : ''}${r.data.already_sent_skipped ? `${r.data.already_sent_skipped} skipped (already had this email). ` : ''}${failed.length ? `FAILED: ${failed.join(', ')} — press Send again to retry just these.` : ''}` });
      load(true);
    } catch (e) {
      setMsg({ ok: false, text: (e.response?.data?.detail || 'Send failed') + ' — nobody gets duplicates: press Send again to continue with the remaining contacts.' });
      load(true);
    } finally { setBusy(''); }
  }

  const insertField = (f) => setSel((s) => ({ ...s, body: `${s.body || ''}${f}` }));

  return (
    <div className="space-y-5" data-testid="finalist-campaigns-panel">
      <div className="flex flex-wrap items-center gap-3">
        <select value={sel?.id || ''} onChange={(e) => { const c = campaigns.find((x) => x.id === e.target.value); if (c) setSel({ ...c }); }}
          data-testid="campaign-select" className="border rounded-xl px-3 py-2 bg-white max-w-full">
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.sent_count} sent)</option>)}
        </select>
        <button onClick={newCampaign} disabled={!!busy} data-testid="campaign-new-btn" className="px-3 py-2 rounded-xl border flex items-center gap-1.5 text-sm"><Plus size={14}/> New email</button>
        {sel && <button onClick={removeCampaign} data-testid="campaign-delete-btn" className="px-3 py-2 rounded-xl border border-heritage/40 text-heritage flex items-center gap-1.5 text-sm"><Trash2 size={14}/> Delete</button>}
      </div>

      {sel && (
        <div className="space-y-4">
          <label className="block text-sm font-medium">Internal name
            <input value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} data-testid="campaign-name-input"
              className="mt-1 block w-full max-w-md border rounded-xl px-3 py-2 font-normal"/>
          </label>
          <label className="block text-sm font-medium">Subject
            <input value={sel.subject} onChange={(e) => setSel({ ...sel, subject: e.target.value })} data-testid="campaign-subject-input"
              className="mt-1 block w-full border rounded-xl px-3 py-2 font-normal"/>
          </label>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-sm font-medium">Email text</span>
              {['{business_name}', '{category}', '{upload_link}', '{tickets_link}', '{video_deadline}'].map((f) => (
                <button key={f} onClick={() => insertField(f)} className="text-xs px-2 py-1 rounded-lg bg-stone border" data-testid={`campaign-merge-${f.replace(/[{}]/g, '')}`}>{f}</button>
              ))}
              <span className="text-xs text-charcoal/50">Links: [text](https://url)</span>
            </div>
            <textarea value={sel.body} onChange={(e) => setSel({ ...sel, body: e.target.value })} rows={16} data-testid="campaign-body-input"
              className="w-full border rounded-xl px-3 py-2 text-sm leading-relaxed"/>
          </div>
          <label className="block text-sm font-medium max-w-md">BCC every email to (optional)
            <input value={sel.bcc || ''} onChange={(e) => setSel({ ...sel, bcc: e.target.value })} data-testid="campaign-bcc-input"
              placeholder="you@example.com" className="mt-1 block w-full border rounded-xl px-3 py-2 font-normal"/>
            <span className="text-xs text-charcoal/50 font-normal">Careful: a BCC copy counts as a second email against your Resend daily quota.</span>
          </label>
          <button onClick={saveCopy} disabled={!!busy} data-testid="campaign-save-btn" className="px-4 py-2 rounded-xl border font-semibold">Save copy</button>

          <div className="p-4 rounded-2xl bg-stone/60 space-y-3">
            <label className="flex items-start gap-2 text-sm font-medium">
              <input type="checkbox" checked={excludeBooked} onChange={(e) => setExcludeBooked(e.target.checked)} data-testid="campaign-exclude-booked-checkbox" className="mt-0.5"/>
              Exclude businesses that have already booked or been invoiced for tickets
            </label>
            <label className="flex items-start gap-2 text-sm font-medium">
              <input type="checkbox" checked={excludeUploaded} onChange={(e) => setExcludeUploaded(e.target.checked)} data-testid="campaign-exclude-uploaded-checkbox" className="mt-0.5"/>
              Exclude businesses that have already uploaded their ceremony video
            </label>
            <label className="flex items-start gap-2 text-sm font-medium">
              <input type="checkbox" checked={resendAlreadySent} onChange={(e) => setResendAlreadySent(e.target.checked)} data-testid="campaign-resend-checkbox" className="mt-0.5"/>
              <span>Send again to businesses that already received this email
                <span className="block text-xs text-charcoal/50 font-normal">Use for follow-up chasers — the exclusion filters above still apply, so with "exclude uploaded" ticked only businesses still missing a video get the reminder.</span>
              </span>
            </label>
            {recips && (
              <div className="text-sm space-y-1" data-testid="campaign-recipient-counts">
                <div><strong>{recips.counts.will_send}</strong> will receive it · {(recips.counts.excluded_booked || 0) + (recips.counts.excluded_uploaded || 0)} excluded by filters{recips.counts.manually_edited ? ` · ${recips.counts.manually_edited} manually edited` : ''} · {recips.counts.already_sent} {resendAlreadySent ? 'previously received it (included again unless excluded)' : 'already had this email'} · {recips.counts.no_email} finalists have no email address</div>
                <button type="button" onClick={() => setShowRecips((s) => !s)} className="underline text-xs" data-testid="campaign-recipients-toggle">{showRecips ? 'Hide' : 'Show'} & edit recipient list</button>
                {showRecips && (
                  <ul className="text-xs space-y-1 max-h-72 overflow-y-auto pr-2" data-testid="campaign-recipients-list">
                    {recips.recipients.map((r) => {
                      const reason = r.override === 'exclude' ? 'removed by you'
                        : r.override === 'include' ? 'added by you'
                        : r.excluded_booked ? 'booked'
                        : r.excluded_uploaded ? 'video received'
                        : r.already_sent ? (resendAlreadySent ? 'sent before — will resend' : 'sent') : '';
                      return (
                        <li key={r.email} className="flex items-center gap-2" data-testid={`campaign-recipient-${r.email}`}>
                          <span className={r.will_send ? '' : 'text-charcoal/40 line-through'}>
                            {r.business} — {r.email}{reason ? ` · ${reason}` : ''}
                          </span>
                          {r.override ? (
                            <button type="button" onClick={() => setOverride(r.email, 'clear')} data-testid={`campaign-recipient-reset-${r.email}`}
                              className="shrink-0 text-[11px] px-2 py-0.5 rounded-lg border text-charcoal/70">Undo</button>
                          ) : r.will_send ? (
                            <button type="button" onClick={() => setOverride(r.email, 'exclude')} data-testid={`campaign-recipient-remove-${r.email}`}
                              className="shrink-0 text-[11px] px-2 py-0.5 rounded-lg border border-heritage/40 text-heritage">Remove</button>
                          ) : (
                            <button type="button" onClick={() => setOverride(r.email, 'include')} data-testid={`campaign-recipient-add-${r.email}`}
                              className="shrink-0 text-[11px] px-2 py-0.5 rounded-lg border border-forest/40 text-forest">Add back</button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <input value={spotTo} onChange={(e) => setSpotTo(e.target.value)} placeholder="your@email.com" data-testid="campaign-spot-input"
                className="border rounded-xl px-3 py-2 text-sm"/>
              <button onClick={spotCheck} disabled={!!busy || !spotTo.trim()} data-testid="campaign-spot-btn"
                className="px-3 py-2 rounded-xl border text-sm flex items-center gap-1.5"><Mail size={14}/> Send me a test</button>
              <button onClick={sendAll} disabled={!!busy || !recips || recips.counts.will_send === 0} data-testid="campaign-send-btn"
                className="px-5 py-2 rounded-xl bg-forest text-cream font-semibold flex items-center gap-2 disabled:opacity-50">
                <Send size={15}/> {busy === 'send' ? 'Sending…' : `Send to ${recips ? recips.counts.will_send : '…'} finalists`}
              </button>
            </div>
            {sel.sent_count > 0 && <div className="text-xs text-charcoal/50 flex items-center gap-1"><Users size={12}/> This email has been sent to {sel.sent_count} business{sel.sent_count === 1 ? '' : 'es'} so far. Created {fmt(sel.created_at)}.</div>}
          </div>
          {msg && <div data-testid="campaign-result" className={`p-3 rounded-xl text-sm ${msg.ok ? 'bg-forest/10 text-forest' : 'bg-heritage/10 text-heritage'}`}>{msg.text}</div>}
        </div>
      )}
    </div>
  );
}
